import type { UnknownAction } from 'redux';
import type { UrgentMessage } from '../../common/graphql/fragments/gql/UrgentMessage';
import type { TrackerCategory } from '../../common/tracking/trackerService';

export type ModalActions =
  | ClearModalsAction
  | HideModalAction
  | ShowIntroductionAction
  | ShowCo2EmissionsAction
  | ShowCustomsLinkGdprAction
  | ShowUrgentMessageAction
  | ShowHotKeyOverviewAction;

interface ModalArgs<TArgs extends Record<string, unknown> = Record<string, unknown>> {
  args: TArgs;
  label: string;
  modalOptions?: {
    shouldCloseOnOverlayClick?: boolean;
    className?: string;
  };
  type: string;
}

export interface UrgentMessageModalArgs
  extends ModalArgs<{
    urgentMessage: UrgentMessage;
    trackerCategory: TrackerCategory;
    trackerAction: string;
  }> {
  label: string;
  type: 'urgentMessage';
}

export interface CustomsLinkGdprModalArgs
  extends ModalArgs<{
    bookingNo: number;
    trackerCategory: TrackerCategory;
  }> {
  type: 'customslink-gdpr';
}

export interface IntroductionVideoModalArgs extends ModalArgs {
  label: string;
  type: 'introduction';
}

export interface Co2EmissionsModalArgs extends ModalArgs {
  label: string;
  type: 'co2emissions';
}

export interface HotKeyOverviewArgs extends ModalArgs {
  label: string;
  type: 'hotkeysOverview';
}

export type ModalDefinition =
  | UrgentMessageModalArgs
  | IntroductionVideoModalArgs
  | Co2EmissionsModalArgs
  | CustomsLinkGdprModalArgs
  | HotKeyOverviewArgs;

export interface ShowUrgentMessageAction extends UnknownAction {
  payload: UrgentMessageModalArgs;
  type: 'MODAL:PUSH_MODAL:URGENT_MESSAGE';
}

export const showUrgentMessage = (
  urgentMessage: UrgentMessage,
  trackerCategory: TrackerCategory,
  trackerAction: string,
): ShowUrgentMessageAction => ({
  payload: {
    args: {
      urgentMessage,
      trackerCategory,
      trackerAction,
    },
    label: '',
    type: 'urgentMessage',
  },
  type: 'MODAL:PUSH_MODAL:URGENT_MESSAGE',
});

export interface ShowIntroductionAction extends UnknownAction {
  payload: IntroductionVideoModalArgs;
  type: 'MODAL:PUSH_MODAL:INTRODUCTION';
}

export const showIntroduction = (): ShowIntroductionAction => ({
  payload: {
    args: {},
    label: '',
    modalOptions: {
      shouldCloseOnOverlayClick: false,
    },
    type: 'introduction',
  },
  type: 'MODAL:PUSH_MODAL:INTRODUCTION',
});

export interface ShowCo2EmissionsAction extends UnknownAction {
  payload: Co2EmissionsModalArgs;
  type: 'MODAL:PUSH_MODAL:CO2_EMISSIONS';
}

export const showCo2Emissions = (): ShowCo2EmissionsAction => ({
  payload: {
    args: {},
    label: '',
    type: 'co2emissions',
  },
  type: 'MODAL:PUSH_MODAL:CO2_EMISSIONS',
});

export interface ShowCustomsLinkGdprAction extends UnknownAction {
  payload: CustomsLinkGdprModalArgs;
  type: 'MODAL:PUSH_MODAL:CUSTOMS_LINK_GDPR';
}

export const showCustomsLinkGdpr = (
  bookingNo: number,
  trackerCategory: TrackerCategory,
): ShowCustomsLinkGdprAction => ({
  payload: {
    label: '',
    args: { bookingNo, trackerCategory },
    type: 'customslink-gdpr',
  },
  type: 'MODAL:PUSH_MODAL:CUSTOMS_LINK_GDPR',
});

interface HideModalAction extends UnknownAction {
  payload: {};
  type: 'MODAL:POP_MODAL';
}

export const hideModal = (): HideModalAction => ({
  payload: {},
  type: 'MODAL:POP_MODAL',
});

interface ClearModalsAction extends UnknownAction {
  payload: {};
  type: 'MODAL:CLEAR_MODALS';
}

export const clearModals = (): ClearModalsAction => ({
  payload: {},
  type: 'MODAL:CLEAR_MODALS',
});

export interface ShowHotKeyOverviewAction extends UnknownAction {
  payload: HotKeyOverviewArgs;
  type: 'MODAL:PUSH_MODAL:HOTKEYS_OVERVIEW';
}

export const showHotkeysOverview = (): ShowHotKeyOverviewAction => ({
  payload: {
    args: {},
    label: 'Hotkeys Overview',
    type: 'hotkeysOverview',
    modalOptions: {
      className: 'tour_shortcuts_overview',
    },
  },
  type: 'MODAL:PUSH_MODAL:HOTKEYS_OVERVIEW',
});
